import { useRef, useEffect, useCallback } from 'react';
import { DBNAME, STORAGE_NO_SPACE } from '../utils/constants';
import { estimateSpaceRemaining } from '../utils/technical-tests/technical-utils';
import useStateRef from '../hooks/useRefState';

const useDiskStorage = ({
	mode = 'readonly',
	checkStorageAvailability = false,
	dataBaseName = DBNAME,
} = {}) => {
	const storageRef = useRef(null);
	const [error, setError] = useStateRef(null, true);

	useEffect(() => {
		if (checkStorageAvailability) {
			(async function getSpace() {
				const availableSpace = await estimateSpaceRemaining();

				if (availableSpace === STORAGE_NO_SPACE) {
					setError(availableSpace);
				}
				return null;
			})();
		}
	}, [checkStorageAvailability, setError]);

	const openDb = useCallback(
		async (callback) => {
			try {
				const DiskStorage = (
					await import(
						/* webpackChunkName: "disk-storage" */
						'../utils/technical-tests/disk-storage'
					)
				).default;

				storageRef.current = await DiskStorage({
					dataBaseName,
					mode,
				});

				if (callback) {
					await callback(storageRef.current);
					storageRef.current.close();
				}
			} catch (err) {
				setError(err);
				storageRef.current?.close();
			}
		},
		[dataBaseName, mode, setError]
	);

	return {
		storage: storageRef.current,
		error: error.current,
		errorRef: error,
		checkStorageAvailability,
		openDb,
	};
};

export default useDiskStorage;
